/**
 * DeliveryOptionsModal Component
 * Main modal for selecting delivery options per supplier before execution
 */

import React, { useState, useMemo, useCallback, useEffect } from "react";
import { useIsMutating } from "@tanstack/react-query";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Box,
  Text,
  VStack,
  HStack,
  Button,
  Badge,
  Flex,
  IconButton,
  Tooltip,
  useDisclosure,
} from "@chakra-ui/react";
import { MdClose } from "react-icons/md";
import {
  DeliveryOptionsModalProps,
  SelectedDeliveryOption,
  EnrichedSupplierForDelivery,
  NormalizedDeliverySelection,
} from "./types";
import { toLocalDateTimeInput } from "../../utils/dateResolvers";
import { useCountdownTimer } from "./hooks/useCountdownTimer";
import EstimateSplitCard from "./EstimateSplitCard/index";
import OrderCancellationModal from "../../views/Orders/components/OrderCancellationModal";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import {
  GetOrderLabels,
  useOrderLabel,
} from "../../views/Orders/components/GaraazOrderCard/OrderLabelContext";
import { calculateDiscountedPrice } from "./utils/deliveryCostUtils";
import {
  calculateRemainingItemsMRP,
  calculateSupplierFulfillingItemsMRP,
} from "./utils/deliveryItemUtils";
import {
  DeliveryModes,
  CPTSlabForManualOption,
} from "../../views/Orders/enums";

dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * Check if a supplier's selection is complete
 * Valid states: Hold, Cancel, BuyTime, or Confirm with valid delivery details
 */
const isSelectionComplete = (
  supplier: EnrichedSupplierForDelivery,
  selection: SelectedDeliveryOption | undefined,
  directDeliveryMinimumDeliveryCost?: number,
): boolean => {
  if (!selection) return false;

  // Hold, Cancel, and BuyTime are always valid actions
  if (selection.customerAction === "hold") return true;
  if (selection.customerAction === "cancel") return true;
  if (selection.customerAction === "buytime") {
    // BuyTime requires a hours value
    return !!selection.buyTimeHours;
  }

  // Confirm requires valid delivery details
  if (selection.customerAction === "confirm") {
    const details = selection.deliveryDetails;
    if (!details) return false;

    if (details.selectionType === "option") {
      const { selectedDeliveryOptionId } = details;
      if (!selectedDeliveryOptionId) return false;
      return supplier.deliveryOptions.some(
        (o) => o._id === selectedDeliveryOptionId,
      );
    }

    if (details.selectionType === "manual") {
      // Manual form is open but not yet saved. The user cannot proceed until it is saved
      // (which changes selectionType to "option") or they select a different option.
      return false;
    }
  }

  return false;
};

const getSupplierKey = (
  supplierId: string | undefined | null,
  index: number
): string => {
  return supplierId ?? `remaining-items-${index}`;
};

/**
 * Normalize a single supplier selection into a lean, downstream-friendly shape.
 * Ensures CPT and delivery cost are present for confirm actions, whether the user
 * selected a predefined option or entered values manually.
 * For buytime actions, calculates the ISO datetime from hours + confirmationTime.
 */
const normalizeSelection = (
  supplier: EnrichedSupplierForDelivery,
  selection: SelectedDeliveryOption | undefined,
  confirmationTime: string
): NormalizedDeliverySelection => {
  if (!selection) {
    // Should not happen when canConfirm is true, but provide a safe fallback.
    return {
      supplierId: supplier.supplierId,
      splitId: supplier.splitId,
      customerAction: "hold",
    };
  }

  const base: NormalizedDeliverySelection = {
    supplierId: selection.supplierId,
    splitId: supplier.splitId,
    customerAction: selection.customerAction,
  };

  // Non-confirm actions: pass through as-is (no CPT / cost needed).
  if (
    selection.customerAction === "hold" ||
    selection.customerAction === "cancel"
  ) {
    return base;
  }

  if (selection.customerAction === "buytime") {
    // Calculate buyTimeValue as confirmationTime + hours (using UTC consistently)
    let buyTimeValue: string | undefined;
    if (selection.buyTimeHours) {
      const hours = parseFloat(selection.buyTimeHours);
      if (!isNaN(hours) && hours > 0) {
        buyTimeValue = dayjs(confirmationTime)
          .add(hours, "hours")
          .toISOString();
      }
    }
    return {
      ...base,
      buyTimeValue,
    };
  }

  // Confirm actions: include CPT and delivery cost in a unified format.
  const details = selection.deliveryDetails;
  if (!details) {
    return base;
  }

  if (details.selectionType === "option") {
    const option = supplier.deliveryOptions.find(
      (o) => o._id === details.selectedDeliveryOptionId,
    );

    if (!option) {
      return base;
    }

    const deliveryCost = calculateDiscountedPrice(
      option.deliveryCost,
      option.deliveryCostDiscountPercentage
    );

    return {
      ...base,
      cptSlab: option.cptSlab,
      cptMin: option.cptMin,
      cptMax: option.cptMax,
      deliveryCost,
      vehicleType: supplier.vehicleType,
      selectionSource: "option",
      deliveryOptionId: option._id,
      deliveryMode: option.deliveryMode,
    };
  }

  if (details.selectionType === "manual") {
    // Should be caught by isSelectionComplete, but providing a failsafe fallback.
    return base;
  }

  return base;
};

const DeliveryOptionsModal: React.FC<DeliveryOptionsModalProps> = ({
  isOpen,
  onClose,
  orderNo,
  suppliers,
  expiresAt,
  estimateNo,
  onShareEstimate,
  isEstimateShared = false,
  maxSplitAllowedShareToCustomer = 1,
  onConfirm,
  onOptionUpdate,
  onOptionAdd,
  onOptionDelete,
  onHoldOrder,
  onClearBucket,
  quickEtasMins,
  onCancelOrder,
  orderCancellationReasons = [],
  directDeliveryMinimumDeliveryCost,
}) => {
  // State for selections (keyed by supplierId or "remaining-items")
  const [selections, setSelections] = useState<
    Map<string, SelectedDeliveryOption>
  >(new Map());
  const isMutatingCount = useIsMutating({ mutationKey: ["deliveryOption"] });
  const isMutating = isMutatingCount > 0;
  const { orderLabel }: { orderLabel: GetOrderLabels } = useOrderLabel();
  // Cancellation modal state
  const {
    isOpen: isCancellationModalOpen,
    onOpen: onCancellationModalOpen,
    onClose: onCancellationModalClose,
  } = useDisclosure();

  // Countdown timer
  const { formattedTime, isExpired } = useCountdownTimer(expiresAt);

  // Auto-close and clear bucket on timer expiry
  useEffect(() => {
    if (isExpired && isOpen) {
      onClearBucket?.();
      onClose();
    }
  }, [isExpired, isOpen, onClearBucket, onClose]);

  // Check if all suppliers have valid selections
  const allSelectionsComplete = useMemo(() => {
    return suppliers.every((supplier, index) => {
      const key = getSupplierKey(supplier.supplierId, index);
      return isSelectionComplete(
        supplier,
        selections.get(key),
        directDeliveryMinimumDeliveryCost,
      );
    });
  }, [suppliers, selections, directDeliveryMinimumDeliveryCost]);

  const canConfirm = !isExpired && allSelectionsComplete && !isMutating;

  const remainingItemsMRP = useMemo(() => {
    return calculateRemainingItemsMRP(suppliers);
  }, [suppliers]);

  const fulfilledItemsMRP = useMemo(() => {
    if (!suppliers || suppliers.length === 0) {
      return 0;
    }

    return suppliers.reduce((total, supplier) => {
      if (supplier.supplierId === null || supplier.supplierId === undefined) {
        return total;
      }
      return total + calculateSupplierFulfillingItemsMRP(supplier);
    }, 0);
  }, [suppliers]);

  // Handle selection change for a supplier
  const handleSelectionChange = useCallback(
    (selection: SelectedDeliveryOption | undefined, index: number) => {
      setSelections((prev) => {
        const next = new Map(prev);
        const supplier = suppliers[index];
        const key = getSupplierKey(supplier?.supplierId, index);

        if (!selection) {
          next.delete(key);
          return next;
        }

        next.set(key, selection);
        return next;
      });
    },
    [suppliers]
  );

  // Handle confirm button click
  const handleConfirm = useCallback(() => {
    // Capture confirmation time - used for calculating buy time ISO datetime
    const confirmationTime = new Date().toISOString();

    const normalizedSelections: NormalizedDeliverySelection[] = suppliers.map(
      (supplier, index) => {
        const key = getSupplierKey(supplier.supplierId, index);
        const selection = selections.get(key);
        return normalizeSelection(supplier, selection, confirmationTime);
      }
    );
    onConfirm(normalizedSelections, suppliers, estimateNo);
  }, [selections, suppliers, onConfirm, estimateNo]);

  // Handle hold order
  const handleHoldOrder = useCallback(async () => {
    if (onHoldOrder) {
      await onHoldOrder();
    }
  }, [onHoldOrder]);

  // Handle cancel order
  const handleCancelOrderSubmit = useCallback(
    async (reason: string) => {
      if (onCancelOrder) {
        await onCancelOrder(reason);
        onClose();
      }
    },
    [onCancelOrder, onClose]
  );

  // Workaround for Chakra UI Modal wheel scroll propagation issues.
  // Manually handles wheel events to ensure consistent scrolling behavior
  // within the modal body and prevent scroll events from bubbling to parent elements.
  const handleBodyWheel = useCallback(
    (event: React.WheelEvent<HTMLDivElement>) => {
      const target = event.currentTarget;
      if (!target) return;
      target.scrollTop += event.deltaY;
      event.preventDefault();
    },
    []
  );

  // Reset selections when modal opens
  useEffect(() => {
    if (isOpen) {
      setSelections(new Map());
    }
  }, [isOpen]);

  const [isSharingEstimate, setIsSharingEstimate] = useState(false);

  const hasSupplierSplitWithNoOptions = useMemo(
    () =>
      suppliers.some(
        (s) => s.supplierId != null && s.deliveryOptions.length === 0,
      ),
    [suppliers],
  );

  const isShareDisabled =
    isEstimateShared ||
    suppliers.length > maxSplitAllowedShareToCustomer ||
    hasSupplierSplitWithNoOptions;

  const shareTooltipLabel = isEstimateShared
    ? "Estimate already shared to customer"
    : hasSupplierSplitWithNoOptions
      ? "Each supplier split must have at least one delivery option before sharing"
      : suppliers.length > maxSplitAllowedShareToCustomer
        ? `Can only share estimates with up to ${maxSplitAllowedShareToCustomer} supplier split(s)`
        : "";

  const handleShareEstimateClick = useCallback(async () => {
    if (!onShareEstimate) return;
    try {
      setIsSharingEstimate(true);
      await onShareEstimate();
    } finally {
      setIsSharingEstimate(false);
    }
  }, [onShareEstimate]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="5xl"
      scrollBehavior="inside"
      isCentered
      closeOnOverlayClick={false}
    >
      <ModalOverlay bg="blackAlpha.600" />
      <ModalContent
        maxH="90vh"
        borderRadius="xl"
        overflow="hidden"
        fontFamily="'Nunito','Inter', sans-serif"
      >
        {/* Header */}
        <ModalHeader
          bg="linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
          borderBottom="none"
          py={4}
          px={6}
          position="relative"
        >
          <Flex justify="space-between" align="center" pr={10}>
            <HStack spacing={3}>
              <Text
                fontSize="1.15rem"
                fontWeight="bold"
                color="white"
                fontFamily="'Nunito','Inter', sans-serif"
              >
                Delivery Options
              </Text>
              <Text
                fontSize="0.9rem"
                color="whiteAlpha.800"
                fontFamily="'Nunito','Inter', sans-serif"
              >
                {orderLabel} #{orderNo}
              </Text>
            </HStack>

            {/* Timer Badge */}
            <HStack
              bg={isExpired ? "orange.400" : "whiteAlpha.200"}
              px={3}
              py={1.5}
              borderRadius="md"
              spacing={2}
              backdropFilter="blur(4px)"
            >
              <Box
                w={2}
                h={2}
                borderRadius="full"
                bg={isExpired ? "white" : "whiteAlpha.800"}
              />
              <Text
                fontSize="0.8rem"
                color="white"
                fontFamily="'Nunito','Inter', sans-serif"
              >
                Options expire in
              </Text>
              <Badge
                bg={isExpired ? "white" : "orange.400"}
                color={isExpired ? "orange.400" : "white"}
                px={2}
                py={0.5}
                borderRadius="md"
                fontSize="0.85rem"
                fontWeight="bold"
              >
                {formattedTime}
              </Badge>
            </HStack>
          </Flex>

          {/* Close Button */}
          <IconButton
            aria-label="Close modal"
            icon={<MdClose size={20} />}
            position="absolute"
            top={4}
            right={4}
            size="sm"
            variant="ghost"
            color="white"
            _hover={{ bg: "whiteAlpha.200" }}
            onClick={onClose}
          />
        </ModalHeader>

        {/* Body with scrollable suppliers */}
        <ModalBody
          bg="#f8f9fc"
          px={6}
          py={4}
          overflowY="auto"
          onWheelCapture={handleBodyWheel}
          css={{
            "&::-webkit-scrollbar": {
              width: "8px",
            },
            "&::-webkit-scrollbar-track": {
              background: "#f1f1f1",
              borderRadius: "4px",
            },
            "&::-webkit-scrollbar-thumb": {
              background: "#c1c1c1",
              borderRadius: "4px",
            },
            "&::-webkit-scrollbar-thumb:hover": {
              background: "#a8a8a8",
            },
          }}
        >
          {/* Section Header */}
          <Box mb={4}>
            <Flex justify="space-between" align="flex-start">
              <Box>
                <Text
                  fontSize="0.95rem"
                  fontWeight="bold"
                  color="gray.700"
                  fontFamily="'Nunito','Inter', sans-serif"
                >
                  Suppliers
                </Text>
                <Text
                  fontSize="0.8rem"
                  color="gray.500"
                  fontFamily="'Nunito','Inter', sans-serif"
                >
                  Review part numbers and confirm CPT and delivery charges.
                </Text>
              </Box>
              <HStack align="center" spacing={2}>
                <Text
                  fontSize="0.9rem"
                  fontWeight="bold"
                  color="gray.700"
                  fontFamily="'Nunito','Inter', sans-serif"
                  whiteSpace="nowrap"
                >
                  MRP:
                </Text>
                <Text
                  fontSize="0.9rem"
                  fontWeight="bold"
                  color="gray.700"
                  fontFamily="'Nunito','Inter', sans-serif"
                  whiteSpace="nowrap"
                >
                  ₹{fulfilledItemsMRP.toLocaleString("en-IN")}
                </Text>
                <Text
                  fontSize="0.9rem"
                  fontWeight="bold"
                  color="gray.700"
                  fontFamily="'Nunito','Inter', sans-serif"
                >
                  /
                </Text>
                <Text
                  fontSize="0.9rem"
                  fontWeight="bold"
                  color="red.700"
                  fontFamily="'Nunito','Inter', sans-serif"
                  whiteSpace="nowrap"
                >
                  ₹{remainingItemsMRP.toLocaleString("en-IN")}
                </Text>
              </HStack>
            </Flex>
          </Box>

          {/* Supplier Cards */}
          <VStack align="stretch" spacing={4}>
            {suppliers.map((supplier, index) => (
              <EstimateSplitCard
                key={getSupplierKey(supplier.supplierId, index)}
                estimateNo={estimateNo}
                supplier={supplier}
                selection={selections.get(
                  getSupplierKey(supplier.supplierId, index)
                )}
                onSelectionChange={(selection) =>
                  handleSelectionChange(selection, index)
                }
                onOptionUpdate={(updatedOption) =>
                  onOptionUpdate(supplier.splitId, updatedOption)
                }
                onOptionAdd={(newOption) =>
                  onOptionAdd(supplier.splitId, newOption)
                }
                onOptionDelete={(optionId) =>
                  onOptionDelete(supplier.splitId, optionId)
                }
                isExpired={isExpired}
                quickEtasMins={quickEtasMins}
                directDeliveryMinimumDeliveryCost={
                  directDeliveryMinimumDeliveryCost
                }
              />
            ))}
          </VStack>
        </ModalBody>

        {/* Footer */}
        <ModalFooter
          bg="white"
          borderTop="1px solid"
          borderColor="gray.200"
          py={3}
          px={6}
        >
          <Flex w="100%" justify="space-between" align="center">
            {/* Left side - Expiry note and Share Estimate button */}
            <HStack spacing={3} align="center">
              {onShareEstimate && (
                <Tooltip
                  label={shareTooltipLabel}
                  isDisabled={!isShareDisabled}
                  hasArrow
                  placement="top"
                  shouldWrapChildren
                >
                  <Button
                    variant="outline"
                    onClick={handleShareEstimateClick}
                    isLoading={isSharingEstimate}
                    isDisabled={isShareDisabled}
                    fontFamily="'Nunito','Inter', sans-serif"
                    fontWeight="600"
                    size="sm"
                    px={3}
                    borderRadius="full"
                    borderColor={isEstimateShared ? "green.400" : "blue.400"}
                    borderWidth="1.5px"
                    color={isEstimateShared ? "green.500" : "blue.500"}
                    bg="white"
                    _hover={
                      isEstimateShared
                        ? {}
                        : {
                            bg: "blue.50",
                            borderColor: "blue.500",
                            color: "blue.600",
                          }
                    }
                    _disabled={{
                      bg: isEstimateShared ? "green.50" : "gray.300",
                      borderColor: isEstimateShared ? "green.400" : "gray.400",
                      color: isEstimateShared ? "green.600" : "gray.500",
                      cursor: "not-allowed",
                      opacity: 1,
                    }}
                    transition="all 0.2s ease"
                  >
                    {isEstimateShared
                      ? "SHARED"
                      : "SHARE ESTIMATE TO CUSTOMER"}
                  </Button>
                </Tooltip>
              )}
              <Text
                fontSize="0.75rem"
                color="gray.500"
                fontFamily="'Nunito','Inter', sans-serif"
                maxW="40%"
              >
                {isExpired
                  ? "Options expired. Modal will close automatically."
                  : ""}
              </Text>
            </HStack>

            {/* Right side - Buttons: Hold Order | Cancel Order | Proceed */}
            <HStack spacing={3}>
              {/* Hold Order Button - Subtle gray/yellow outline */}
              {onHoldOrder && (
                <Button
                  variant="outline"
                  onClick={handleHoldOrder}
                  fontFamily="'Nunito','Inter', sans-serif"
                  fontWeight="600"
                  size="sm"
                  px={3}
                  borderRadius="full"
                  borderColor="yellow.500"
                  color="yellow.500"
                  bg="white"
                  _hover={{
                    bg: "yellow.50",
                    borderColor: "yellow.400",
                    color: "yellow.700",
                  }}
                  transition="all 0.2s ease"
                >
                  HOLD ESTIMATE
                </Button>
              )}
              {/* Cancel Order Button - Red outline */}
              {onCancelOrder && (
                <Button
                  variant="outline"
                  onClick={onCancellationModalOpen}
                  fontFamily="'Nunito','Inter', sans-serif"
                  fontWeight="600"
                  size="sm"
                  px={3}
                  borderRadius="full"
                  borderColor="red.400"
                  borderWidth="1.5px"
                  color="red.500"
                  bg="white"
                  _hover={{
                    bg: "red.50",
                    borderColor: "red.500",
                    color: "red.600",
                  }}
                  transition="all 0.2s ease"
                >
                  CANCEL ESTIMATE
                </Button>
              )}
              {/* Proceed Button - Blue filled */}
              <Button
                bg="#4299E1"
                color="white"
                isDisabled={!canConfirm}
                onClick={handleConfirm}
                fontFamily="'Nunito','Inter', sans-serif"
                fontWeight="600"
                size="sm"
                px={3}
                borderRadius="full"
                _hover={{
                  bg: "#3182CE",
                  transform: "translateY(-1px)",
                  shadow: "md",
                }}
                _active={{
                  bg: "#2B6CB0",
                  transform: "translateY(0)",
                }}
                _disabled={{
                  bg: "gray.300",
                  color: "gray.500",
                  cursor: "not-allowed",
                  _hover: {
                    bg: "gray.300",
                    transform: "none",
                    shadow: "none",
                  },
                }}
                transition="all 0.2s ease"
              >
                PROCEED
              </Button>
            </HStack>
          </Flex>
        </ModalFooter>
      </ModalContent>

      {/* Order Cancellation Modal */}
      {onCancelOrder && (
        <OrderCancellationModal
          isOpen={isCancellationModalOpen}
          onClose={onCancellationModalClose}
          orderCacellationReasons={orderCancellationReasons}
          handleSubmit={handleCancelOrderSubmit}
        />
      )}
    </Modal>
  );
};

export default DeliveryOptionsModal;
